import React from 'react';
import { useTranslation } from 'react-i18next';
import { reimbursementModStyles } from './ReimbursementMod.styles';
import { Grid, Typography } from '@mui/material';
import { Category } from './Category';
import { Description } from './Description';
import { ReceiptsPay } from './ReceiptsPay';
import { useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectExpandedModsDialog,
  selectIsEditMod,
  selectPayeeList,
  selectReimbursementCategoryAndFrequency,
  selectPayPackageStandardValues,
} from 'store/redux-store/placement-mods/selectors';
import { Payable } from './Payable';
import { Frequency } from './Frequency';
import { CapAmount } from './CapAmount';
import { MiscellaneousDescription } from './MiscellaneousDescription';
import { StopDate } from './StopDate';
import { AmountPerMile } from './AmountPerMile';
import { useWatch } from 'react-hook-form';
import { CategoryDescriptionOptions, CategoriesOptions, isTransportationCarAllowance } from './ReimbursementHelper';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { isNegativeStatus } from 'app/components/Placement/NextPlacementStatus/StatusDefaults';

export const ReimbursementMod = () => {
  const { t } = useTranslation();
  const isExpanded = useSelector(selectExpandedModsDialog);
  const { classes } = reimbursementModStyles({ isExpanded });
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const payables = useSelector(selectPayeeList);
  const reimbursementCategoryAndFrequency = useSelector(selectReimbursementCategoryAndFrequency);
  const payPackageStandardValues = useSelector(selectPayPackageStandardValues);
  const isModEdit = useSelector(selectIsEditMod);
  const reimbursementCategoryId = useWatch<any>({ name: 'reimbursementCategoryId' });
  const reimbursementDescriptionId = useWatch<any>({ name: 'reimbursementDescriptionId' });
  const showMiscDescription = () => {
    if (
      reimbursementDescriptionId === CategoryDescriptionOptions.MiscellaneousOther ||
      reimbursementDescriptionId === CategoryDescriptionOptions.QualityServicesMiscellaneousMedical
    )
      return true;
    return false;
  };
  const placementStatus = useSelector(selectPlacementStatus);
  const placementStatusIsPositive = (candidatePlacementStatus) => {
    if (!candidatePlacementStatus?.currentPlacementStatus) return false;
  
    return !isNegativeStatus(candidatePlacementStatus?.currentPlacementStatus);
  };
  // look for specific description in Miscellaneous category
  const miscellaneousCategoryHasSpecificDescription = () => {
    if (isTransportationCarAllowance(reimbursementCategoryId, reimbursementDescriptionId))
      return true;

    const isMiscellaneousCategory =
      reimbursementCategoryId === CategoriesOptions.Miscellaneous;
  
    const isSpecificDescription =
      reimbursementDescriptionId === CategoryDescriptionOptions.MiscellaneousCarAllowance ||
      reimbursementDescriptionId === CategoryDescriptionOptions.MiscellaneousMealAllowance ||
      reimbursementDescriptionId === CategoryDescriptionOptions.MiscellaneousShiftCompletionBonus || 
      reimbursementDescriptionId === CategoryDescriptionOptions.MiscellaneousCellPhoneStipend;
  
    return isMiscellaneousCategory && isSpecificDescription;
  }; 
const componentShouldUseDefaultValues = () => { 
    return miscellaneousCategoryHasSpecificDescription() &&
	placementStatusIsPositive(placementStatus);
  };
  const getDefaultAmount = () => {
    let defaultValue = null;
    switch(reimbursementDescriptionId) {
      case CategoryDescriptionOptions.MiscellaneousCarAllowance:
        defaultValue = payPackageStandardValues?.find(v => v.description === 'TLACarAllowance')?.value;
        break;
      case CategoryDescriptionOptions.MiscellaneousMealAllowance:
        defaultValue = payPackageStandardValues?.find(v => v.description === 'TLAMealAllowance')?.value;
        break;
      case CategoryDescriptionOptions.MiscellaneousShiftCompletionBonus:
        defaultValue = '';
        break;  
      case CategoryDescriptionOptions.MiscellaneousCellPhoneStipend:
        defaultValue = payPackageStandardValues?.find(v => v.description === 'TLACellPhoneStipend')?.value;
        break;              
    }
    return defaultValue;
  }
  return (
    <Grid>
      <Grid sx={{ width: isExpanded ? '350px' : '249px' }}>
        <Category options={reimbursementCategoryAndFrequency} isModEdit={isModEdit} />
      </Grid>
      <Grid className={classes.gridRow}>
        <Description options={reimbursementCategoryAndFrequency} isModEdit={isModEdit} />
      </Grid>
      {reimbursementCategoryId !== 5 && (
        <Grid className={classes.gridRow}>
          <ReceiptsPay 
            isExpanded={isExpanded} 
            isModEdit={isModEdit}
            onDescriptionChange={(isDirty) => {
            }}
          />
        </Grid>
      )}
      <Grid className={classes.smallGridRow}>
        <CapAmount 
          isExpanded={isExpanded} 
          isModEdit={isModEdit} 
          defaultAmount={getDefaultAmount()} 
        />
      </Grid>
      <Grid className={classes.gridRow}>
        <Payable 
          options={payables} 
          classes={classes} 
          isModEdit={isModEdit}
          useDefaultPayableValues={componentShouldUseDefaultValues()}
          onDescriptionChange={(isDirty) => {
          }}
        />
      </Grid>
      <Grid className={classes.smallGridRow}>
        <Frequency options={reimbursementCategoryAndFrequency} 
                   isModEdit={isModEdit} 
                   useDefaultFrequencyValues={componentShouldUseDefaultValues()} />
      </Grid>
      <Grid className={classes.gridRow}>
        <StopDate isModEdit={isModEdit} />
      </Grid>
      {reimbursementCategoryId === 7 && reimbursementDescriptionId === 60 && (
        <Grid className={classes.gridRow}>
          <AmountPerMile isExpanded={isExpanded} isModEdit={isModEdit} />
        </Grid>
      )}
      {showMiscDescription() && (
        <Grid className={classes.gridRow}>
          <MiscellaneousDescription isModEdit={isModEdit} showMiscDescription ={showMiscDescription}/>
        </Grid>
      )}
      <Grid className={classes.gridRow}>
        <Grid item xs={12} sx={{ mb: '105px' }}>
          <Typography component="span" className={classes.providerLb}>{`${t(
            'placement.placementMods.billableClient',
          )}`}</Typography>
          <Typography component="span">{selectedModsDetails?.billable ? 'Yes' : 'No'}</Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

